import React from "react";
import "./Everett.css";

export default function Everett() {
  return (
    <article className="ev-page" id="everett-blog">
      {/* Header: title block ABOVE the image */}
      <header className="ev-hero">
        <div className="ev-hero-content">
          <p className="ev-eyebrow">Carpet Care • Everett, WA</p>
          <h1 className="ev-title">
            Early Signs You Need Expert Residential Carpet Cleaners in Everett
          </h1>
          <p className="ev-meta">Updated Nov 2025 • 6 min read</p>
        </div>

        {/* Landscape hero image placed BELOW the title */}
        <figure className="ev-hero-media" aria-label="Article hero image">
          <img
            src={`${process.env.PUBLIC_URL}/blogone.png`}
            alt="Close-up of carpet fibers in an Everett home"
            className="ev-hero-img"
            loading="eager"
            decoding="async"
          />
        </figure>
      </header>

      {/* Body */}
      <div className="ev-container">
        <section className="ev-intro">
          <p>
            Carpets make homes in Everett feel cozy, but they also take in more
            dirt than most people realize. Dust, food crumbs, pet dander, and
            moisture can hide deep within the fibers. Even with weekly
            vacuuming, buildup can quietly grow until it affects the way your
            carpet looks, smells, and feels.
          </p>
          <p>
            If your floors no longer look as fresh as they used to, it might be
            time to call the <strong>best residential carpet cleaners</strong>{" "}
            for a deeper clean that restores comfort and beauty.
          </p>
        </section>

        <section className="ev-section">
          <h2 className="ev-h2">Subtle Clues You Might Be Overlooking</h2>
          <p>
            Some carpet issues happen gradually, which makes them easy to miss.
            Watch out for these early signs that your carpet needs professional
            attention:
          </p>

          <ol className="ev-numbered">
            <li>
              <div className="ev-numbered-content">
                <h3>1. The Carpet No Longer Feels Soft</h3>
                <p>
                  After years of foot traffic, fibers can become compacted and
                  rough. Dirt particles grind against the fibers every time
                  someone walks over them, wearing them down faster. If your
                  carpet feels coarse even after vacuuming, it's likely due to
                  hidden buildup that only professional cleaning can remove.
                </p>
              </div>
            </li>
            <li>
              <div className="ev-numbered-content">
                <h3>2. Spots Reappear After Cleaning</h3>
                <p>
                  You clean a spill, it looks fine—and then a few days later the
                  stain returns. Moisture or residue trapped beneath the carpet
                  backing migrates to the surface. The{" "}
                  <strong>best residential carpet cleaners</strong> use powerful
                  extraction to remove deep-seated dirt and moisture, helping
                  prevent stains from reappearing.
                </p>
              </div>
            </li>
            <li>
              <div className="ev-numbered-content">
                <h3>3. The Colors Look Uneven</h3>
                <p>
                  Patchy or faded areas often come from uneven soil buildup or
                  cleaning with the wrong products. Pros balance pH and choose
                  the right solutions for each fiber type to bring back natural
                  color without damage.
                </p>
              </div>
            </li>
          </ol>
        </section>

        <section className="ev-section">
          <h2 className="ev-h2">The Effects of Skipping Professional Care</h2>
          <ul className="ev-bullets">
            <li>
              <div className="ev-bullet-content">
                <strong>Fibers break down faster:</strong> Grit acts like fine
                sandpaper, cutting carpet fibers from within. Over time this makes
                carpet look older than it is and leads to early replacement.
              </div>
            </li>
            <li>
              <div className="ev-bullet-content">
                <strong>Odors build up inside the padding:</strong> Surface
                cleaning can't reach where bacteria and mildew grow. Hot-water
                extraction from trained cleaners helps remove hidden odors
                completely.
              </div>
            </li>
            <li>
              <div className="ev-bullet-content">
                <strong>Air quality declines:</strong> Everett's damp weather and
                seasonal pollen can cause dust and allergens to build up. Deep
                cleaning helps reduce sneezing, coughing, and other
                allergy-related issues.
              </div>
            </li>
          </ul>
        </section>

        <section className="ev-section">
          <h2 className="ev-h2">When DIY Cleaning Isn't Enough</h2>
          <p>
            Store-bought machines and rental cleaners might seem convenient, but
            they often leave too much water or detergent behind. That residue
            attracts new soil and can make carpets resoil faster. Professionals
            use high-efficiency systems designed to extract dirt and moisture
            completely and treat delicate or high-end fibers safely.
          </p>
        </section>

        <section className="ev-section">
          <h2 className="ev-h2">What to Expect From Professional Cleaning</h2>
          <ol className="ev-steps">
            <li>
              <div className="ev-step-content">
                <strong>Pre-inspection</strong>: The technician checks for stains,
                wear, and carpet type.
              </div>
            </li>
            <li>
              <div className="ev-step-content">
                <strong>Pre-vacuuming</strong>: Removes surface dust and loose
                debris.
              </div>
            </li>
            <li>
              <div className="ev-step-content">
                <strong>Pretreatment</strong>: A safe cleaning solution is applied
                to loosen dirt.
              </div>
            </li>
            <li>
              <div className="ev-step-content">
                <strong>Agitation</strong>: Fibers are gently worked to bring soil
                to the surface.
              </div>
            </li>
            <li>
              <div className="ev-step-content">
                <strong>Hot Water Extraction</strong>: Deep rinsing removes dirt,
                bacteria, and cleaner residue.
              </div>
            </li>
            <li>
              <div className="ev-step-content">
                <strong>Final Grooming</strong>: Fibers are brushed for a uniform
                finish and faster dry.
              </div>
            </li>
          </ol>
          <div className="ev-note">
            This careful process helps extend carpet life while improving
            appearance and cleanliness.
          </div>
        </section>

        <section className="ev-section">
          <h2 className="ev-h2">
            Choosing the Best Residential Carpet Cleaners in Everett
          </h2>
          <p>Look for professionals who:</p>
          <ul className="ev-bullets">
            <li>
              <div className="ev-bullet-content">Specialize in residential carpet care</div>
            </li>
            <li>
              <div className="ev-bullet-content">Use eco-friendly, non-toxic cleaning products</div>
            </li>
            <li>
              <div className="ev-bullet-content">Have experience with various carpet materials</div>
            </li>
            <li>
              <div className="ev-bullet-content">Offer clear pricing and explain their process</div>
            </li>
          </ul>
          <p>
            The <strong>best residential carpet cleaners</strong> in Everett
            understand our moisture-heavy climate and how it affects home
            flooring. Their experience helps prevent mold growth, fiber damage,
            and color fading.
          </p>
        </section>

        <aside className="ev-cta">
          <h2 className="ev-cta-title">
            Keep Your Carpet Looking New with GreenWorks Carpet Cleaning
          </h2>
          <p>
            If your carpet shows any of the signs above, scheduling professional
            service can restore softness and color while protecting it from
            future damage. GreenWorks Carpet Cleaning serves Everett with
            eco-safe products, advanced equipment, and friendly technicians.
          </p>
          <a href="/contact" className="ev-button">
            Book Everett Carpet Cleaning
          </a>
        </aside>
      </div>
    </article>
  );
}